import React, {useEffect, useRef, useState} from 'react';
import {Button, Col, Input, notification, PageHeader, Row, Table, Upload} from 'antd';
import {ReloadOutlined, SearchOutlined, UploadOutlined} from '@ant-design/icons';
import _ from "lodash";
import {getD365Expenses, uploadD365ExpensesFile} from '../../api/d365Api';
import moment from "moment/moment";
import {PREFERRED_DATETIME_FORMAT} from "../../util/time";
import {useUserPermissions} from '../../auth/userHooks';
import {getPaginationConfig} from "../../util/showAllPagination";
import {useDynamicTableHeight} from "../../util/useDynamicTableHeight";


const Expenses = () => {
  const permissions = useUserPermissions();
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const tableContainerRef = useRef(null);
  const tableHeight = useDynamicTableHeight(tableContainerRef);
  const columns = [
    {
      title: 'Expense ID',
      key: 'ID',
      dataIndex: 'd365_identifier',
      sorter: (a, b) => a.d365_identifier.localeCompare(b.d365_identifier),
      render: i => i ? i : '-'
    },
    {
      title: 'Created At',
      dataIndex: 'CreatedAt',
      sorter: (a, b) => a.CreatedAt.localeCompare(b.CreatedAt),
      width: '200px',
      render: i => i ? moment.utc(i).local().format(PREFERRED_DATETIME_FORMAT) : '-'
    },
  ];

  const filterExpenses = (expenses, searchText) => {
    setFilteredExpenses(
      _.chain(expenses)
        .filter(
          (u) =>
            searchText === "" ||
            _.includes(_.toLower(u.d365_identifier), _.toLower(searchText))
        )
        .value()
    );
  };

  const search = (text) => {
    filterExpenses(expenses, text);
  };

  const uploadExpensesFile = file => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (reader.result) {
        setUploading(true);
        const data = _.replace(reader.result, 'data:text/csv;base64,', '');
        uploadD365ExpensesFile(data).then(() => {
          notification.success({
            message: 'Success',
            description: `Expenses file has been uploaded!`,
            placement: 'bottomRight',
          });
        }).finally(() => {
          setUploading(false);
        })
      }
    };
  };

  const uploadProps = {
    beforeUpload: file => {
      setFileList([]);

      if (file && file.type === 'text/csv') {
        uploadExpensesFile(file);
      } else {
        notification.error({
          message: 'Error',
          description: `Expenses file must be in CSV format`,
          placement: 'bottomRight',
        });
      }

      return false;
    },
    fileList,
    maxCount: 1
  };

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const expenses = await getD365Expenses();
      setExpenses(expenses);
      filterExpenses(expenses);
    } catch (e) {
      console.error(`Could not get expenses`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExpenses();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Row>
        <Col span={12}>
          <PageHeader
            title="Expenses"
            subTitle={`(${filteredExpenses.length} shown)`}
            style={{padding: 0}}
          />
        </Col>
      </Row>

      <Row style={{marginBottom: 10}}>
        <Col span={16} style={{marginRight: 10}}>
          <Input
            style={{maxWidth: 400}}
            placeholder="Search for expenses by ID"
            prefix={<SearchOutlined/>}
            onChange={(e) => search(e.target.value)}
            allowClear
          />
        </Col>
        <Col flex="auto"></Col>
        {permissions && permissions.canManageExpenses && (
          <Col style={{marginRight: 10}}>
            <Upload {...uploadProps}>
              <Button loading={uploading} icon={<UploadOutlined/>}>Upload Expenses CSV</Button>
            </Upload>
          </Col>
        )}
        <Col>
          <Button
            style={{float: 'right'}}
            onClick={() => loadExpenses()}
            loading={loading}
            icon={<ReloadOutlined/>}
          >
            Refresh
          </Button>
        </Col>
      </Row>

      <Table
        ref={tableContainerRef}
        rowKey="ID"
        dataSource={filteredExpenses}
        columns={columns}
        pagination={getPaginationConfig(filteredExpenses.length)}
        size="small"
        loading={loading}
        scroll={{y: tableHeight}}
      />
    </>
  );
};

export {Expenses};
